// apps/app/src/components/roadmap/RoadmapBoardContent.tsx
//
// The Roadmap tab on /admin/roadmap (franklycatering #564). Adapted from the
// partner-facing view at apps/marketing/src/views/RoadmapPage.tsx, which reads the
// same GitHub-derived data.
//
// Two deliberate differences from the marketing view:
//   - No partner-facing header prose. Wrong register for an internal admin tab.
//   - No per-item GitHub links. This repo is private, so a link would 404 for every
//     staff member without repo access. The issue number is kept as muted text, which
//     is all the link was load-bearing for (telling apart items that deliberately
//     share a hand-authored description).
//
// It also adds states the marketing view has no need for: a loading skeleton, an
// explicit empty state (the marketing components return null per empty phase AND per
// empty bucket, so an empty dataset would render a blank page), and an error state
// with a retry.

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { QueryLoadError } from "@/components/QueryLoadError";
import { deriveLoadState } from "@/lib/queryLoadState";
import { useRoadmap, type RoadmapItem, type RoadmapItemStatus, type RoadmapPhase } from "@/hooks/useRoadmap";

const STATUS_LABEL: Record<RoadmapItemStatus, string> = {
  shipped: "Shipped",
  "in-review": "In review",
  planned: "Planned",
};

const STATUS_VARIANT: Record<RoadmapItemStatus, "default" | "secondary" | "outline"> = {
  shipped: "default",
  "in-review": "secondary",
  planned: "outline",
};

function ItemRow({ item }: { item: RoadmapItem }) {
  return (
    <li className="flex items-start justify-between gap-4 border-b border-border py-3 last:border-b-0">
      <p className="text-sm text-foreground">
        {item.description}
        <span className="ml-2 text-xs text-muted-foreground">#{item.number}</span>
      </p>
      <Badge variant={STATUS_VARIANT[item.status]} className="shrink-0">
        {STATUS_LABEL[item.status]}
      </Badge>
    </li>
  );
}

function PhaseSection({ phase }: { phase: RoadmapPhase }) {
  if (phase.items.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-xl">{phase.title}</CardTitle>
          <Badge variant="outline">{phase.monthLabel}</Badge>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{phase.theme}</p>
        {phase.caveat && (
          <p className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
            {phase.caveat}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <ul>
          {phase.items.map((item) => (
            <ItemRow key={item.number} item={item} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function BucketGroup({ label, phases }: { label: string; phases: RoadmapPhase[] }) {
  const withItems = phases.filter((p) => p.items.length > 0);
  if (withItems.length === 0) return null;

  return (
    <section className="space-y-4">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{label}</h2>
      {withItems.map((phase) => (
        <PhaseSection key={phase.milestoneNumber} phase={phase} />
      ))}
    </section>
  );
}

export function RoadmapBoardContent() {
  const { data, isLoading, isError, isPending, isPaused, refetch, isFetching } = useRoadmap();
  const loadState = deriveLoadState({ isPending, isError, isPaused });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </div>
    );
  }

  // Two shapes reach this branch and both get the same affordance:
  //   (a) `data.error` — a 200 carrying one of the function's friendly messages
  //       (token not configured, GitHub unreachable).
  //   (b) `isError` — a thrown transport/HTTP failure, which is how a 401/403 from
  //       requireStaff arrives.
  // Handling only (a) would render a permission or network failure as a successful
  // empty roadmap. The retry matters because the hook holds (a) as successful data for
  // staleTime (5 min) with refetchOnWindowFocus off, so without this button a transient
  // GitHub blip would persist until a full page reload.
  if (isError || data?.error) {
    return (
      <QueryLoadError
        className="rounded-lg border border-dashed p-8 text-center"
        failedText={data?.error ?? "Couldn't load the roadmap."}
        offlineText="You're offline. The roadmap will load when your connection is back."
        state={loadState}
        query={{ isFetching, refetch }}
      />
    );
  }

  const phases = data?.phases ?? [];
  const hasAnyItem = phases.some((p) => p.items.length > 0);

  if (!hasAnyItem) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          No roadmap items yet. Items appear here once they're labelled{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">theme:roadmap</code> and
          attached to a milestone.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <BucketGroup label="Recent work" phases={phases.filter((p) => p.bucket === "shipped")} />
      <BucketGroup label="Now" phases={phases.filter((p) => p.bucket === "now")} />
      <BucketGroup label="Next" phases={phases.filter((p) => p.bucket === "next")} />
      <BucketGroup label="Later" phases={phases.filter((p) => p.bucket === "later")} />
    </div>
  );
}
